import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Grid, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import { toast } from 'react-toastify';
import { Address, AddressPayload, Country } from '../../../../models/supplierAdministration';
import { SupplierAdministrationApiError, supplierAdministrationApi } from '../../../../services/supplierAdministrationApi';
import { CustomPaper } from '../../styles';
import AddressDialog from './AddressDialog';
import ConfirmDialog from './ConfirmDialog';

type AddressesTabProps = {
	canModify: boolean;
	countries: Country[];
}

const AddressesTab = ({ canModify, countries }: AddressesTabProps) => {
	const [rows, setRows] = useState<Address[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [appliedSearch, setAppliedSearch] = useState('');
	const [countryFilter, setCountryFilter] = useState<Country | null>(null);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
	const [dialogAddress, setDialogAddress] = useState<Address | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);
	const [deleteLoading, setDeleteLoading] = useState(false);

	const loadAddresses = useCallback(() => {
		setLoading(true);
		setError(null);
		supplierAdministrationApi.listAddresses({
			page: paginationModel.page + 1,
			limit: paginationModel.pageSize,
			searchTerm: appliedSearch || undefined,
			countryId: countryFilter?.id,
		})
			.then((response) => {
				setRows(response.data);
				setTotal(response.total);
			})
			.catch((error: Error) => setError(error.message))
			.finally(() => setLoading(false));
	}, [appliedSearch, countryFilter?.id, paginationModel.page, paginationModel.pageSize]);

	useEffect(() => {
		loadAddresses();
	}, [loadAddresses]);

	const submitSearch = () => {
		setPaginationModel((current) => ({ ...current, page: 0 }));
		setAppliedSearch(search.trim());
	};

	const openCreateDialog = () => {
		setDialogAddress(null);
		setDialogOpen(true);
	};

	const openEditDialog = (address: Address) => {
		setDialogAddress(address);
		setDialogOpen(true);
	};

	const submitAddress = async (payload: AddressPayload) => {
		if (dialogAddress) {
			await supplierAdministrationApi.updateAddress(dialogAddress.id, payload);
			toast.success("L'adresse a bien été modifiée.", { position: 'bottom-center', theme: 'colored' });
		} else {
			await supplierAdministrationApi.createAddress(payload);
			toast.success("L'adresse a bien été créée.", { position: 'bottom-center', theme: 'colored' });
		}
		loadAddresses();
	};

	const confirmDelete = async () => {
		if (!addressToDelete) return;

		setDeleteLoading(true);
		try {
			await supplierAdministrationApi.deleteAddress(addressToDelete.id);
			toast.success("L'adresse a bien été supprimée.", { position: 'bottom-center', theme: 'colored' });
			setAddressToDelete(null);
			loadAddresses();
		} catch (error: any) {
			const message =
				error instanceof SupplierAdministrationApiError && error.statusCode === 409
					? "Cette adresse ne peut pas être supprimée, car elle est encore référencée."
					: error?.message || 'Une erreur est survenue.';
			toast.error(message, { position: 'bottom-center', theme: 'colored' });
		} finally {
			setDeleteLoading(false);
		}
	};

	const columns = useMemo<GridColDef<Address>[]>(
		() => [
			{ field: 'name', headerName: 'Nom', flex: 0.8, minWidth: 180 },
			{ field: 'address', headerName: 'Adresse', flex: 1, minWidth: 220 },
			{ field: 'postalCode', headerName: 'Code postal', flex: 0.4, minWidth: 130 },
			{ field: 'city', headerName: 'Ville', flex: 0.7, minWidth: 160 },
			{
				field: 'country',
				headerName: 'Pays',
				flex: 0.5,
				minWidth: 140,
				valueGetter: (_, row) => row.country ? `${row.country.name} (${row.country.code})` : 'Non renseigné',
			},
			{ field: 'email', headerName: 'Email', flex: 0.7, minWidth: 180 },
			{ field: 'phone', headerName: 'Téléphone', flex: 0.5, minWidth: 140 },
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Actions',
				flex: 0.4,
				minWidth: 110,
				getActions: ({ row }) => [
					<GridActionsCellItem
						key="edit"
						icon={<EditIcon />}
						label="Modifier"
						disabled={!canModify}
						onClick={() => openEditDialog(row)}
					/>,
					<GridActionsCellItem
						key="delete"
						icon={<DeleteIcon />}
						label="Supprimer"
						disabled={!canModify}
						onClick={() => setAddressToDelete(row)}
					/>,
				],
			},
		],
		[canModify]
	);

	return (
		<CustomPaper>
			<Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
				<Grid size={{ xs: 12, md: 3 }}>
					<Typography variant="h5" color="primary">Adresses</Typography>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField
						label="Recherche"
						value={search}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') submitSearch();
						}}
						fullWidth
						size="small"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<Autocomplete
						options={countries}
						value={countryFilter}
						onChange={(_, newValue) => {
							setCountryFilter(newValue);
							setPaginationModel((current) => ({ ...current, page: 0 }));
						}}
						getOptionLabel={(option) => `${option.name} (${option.code})`}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderInput={(params) => <TextField {...params} label="Pays" size="small" />}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 3 }}>
					<Box display="flex" gap={1} justifyContent="flex-end">
						<Button variant="contained" onClick={submitSearch}>Rechercher</Button>
						<Button variant="contained" color="success" disabled={!canModify} onClick={openCreateDialog}>Créer</Button>
					</Box>
				</Grid>
			</Grid>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			<DataGrid
				rows={rows}
				columns={columns}
				loading={loading}
				rowCount={total}
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[10, 25, 50]}
				disableRowSelectionOnClick
				localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
				autoHeight
			/>

			<AddressDialog
				open={dialogOpen}
				addressToEdit={dialogAddress}
				countries={countries}
				onClose={() => setDialogOpen(false)}
				onSubmit={submitAddress}
			/>
			<ConfirmDialog
				open={!!addressToDelete}
				title="Supprimer l'adresse"
				description={`Supprimer l'adresse ${addressToDelete?.name || ''} ? Cette action est définitive.`}
				confirmLabel="Supprimer"
				confirmColor="error"
				loading={deleteLoading}
				onClose={() => setAddressToDelete(null)}
				onConfirm={confirmDelete}
			/>
		</CustomPaper>
	);
};

export default AddressesTab;
